import { Component, Input, OnDestroy, OnInit, ViewChild, ElementRef, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import moment from 'moment-timezone';
import { takeUntil } from 'rxjs/operators';
import { OrderShuttlePlanReportVM, OrderShuttleArrivalDeparture, OrderShuttlePlanDay } from '../../../../../models/OrderShuttlePlanning.models';
import { OrderShuttleService } from '../../../../../services/ordershuttle.service';
import { DatePipe } from '@angular/common';
import { BackOffice24OrderShuttlePlanningDetailsComponent } from '../back-office24-order-shuttle-planning-details/back-office24-order-shuttle-planning-details.component';
import { MyConfigOrganization } from '../../../../../models/myConfig';
import { MyConfigService } from '../../../../../services/my-config.service';
import { BackOffice24StatesService } from '../../state/back-office24-states.service';

@Component({
  selector: 'app-back-office24-order-shuttle-planning',
  templateUrl: './back-office24-order-shuttle-planning.component.html',
  styleUrl: './back-office24-order-shuttle-planning.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BackOffice24OrderShuttlePlanningComponent implements OnInit, OnDestroy {
  @Input() organization: MyConfigOrganization;
  private orgSubscription: Subscription = new Subscription();
  dateFrom: string;
  dateTo: string;
  orderShuttlePlanningForm: UntypedFormGroup;
  orderShuttlePlanReportVM: OrderShuttlePlanReportVM;
  isLoading = false;
  componentDestroyed$: Subject<boolean> = new Subject();
  displayedColumnsShuttle: string[] = [
    'totalOrders',
    'totalPassengers',
    // 'totalBaggage',
    // 'totalChildseats',
    'totalCars',
    'totalFlights'];
  displayedColumnsTid: string[] = ['Tid'];
  @ViewChildren('timeCell', { read: ElementRef }) timeCells!: QueryList<ElementRef>;
  maxHeight: any;
  vehicleFilter: string = 'all';

  ngAfterViewChecked() {
    this.adjustCellHeights();
  }

  adjustCellHeights() {
    const cellHeights: number[] = [];

    this.timeCells.forEach(cell => {
      const height = cell.nativeElement.getBoundingClientRect().height;
      cellHeights.push(height);
    });

    const maxHeight = Math.max(...cellHeights);
    this.maxHeight = `${maxHeight}px`;
  }

  public formatter = Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
  });

  constructor(
    public orderShuttleService: OrderShuttleService,
    public dialog: MatDialog,
    public router: Router,
    private fb: UntypedFormBuilder,
    public datepipe: DatePipe,
    private route: ActivatedRoute,
    private myConfigService: MyConfigService,
    public backOffice24StatesService: BackOffice24StatesService,
  ) { }

  ngOnInit(): void {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
      org => this.organization = org
    );
    this.initOrderShuttlePlanningForm();
    this.initShuttles();
  }

  initShuttles() {
    this.dateFrom = moment().format();
    this.dateTo = moment().add(10, 'd').format();
    this.initOrderShuttlePlanningForm();
    this.getOrderShuttlesPlanning();
  }

  initOrderShuttlePlanningForm() {
    this.orderShuttlePlanningForm = this.fb.group({
      dateFrom: [this.dateFrom],
      dateTo: [this.dateTo],
    });
  }

  getOrderShuttlesPlanning() {
    this.isLoading = true;
    this.setDateFormats();
    this.orderShuttlePlanReportVM = null;
    this.orderShuttleService
      .getOrderShuttlesPlanning(this.dateFrom, this.dateTo)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(orderShuttlePlanReportVM => {
        if (orderShuttlePlanReportVM?.orderShuttlePlanDays) {
          // Gather master lists from all hours returned by the API
          let allArrivalRows: any[] = [];
          let allDepartureRows: any[] = [];

          orderShuttlePlanReportVM.orderShuttlePlanDays.forEach(day => {
            if (day.orderShuttlePlanHours) {
              day.orderShuttlePlanHours.forEach(h => {
                if (h.orderShuttleArrival?.orderShuttlePlanRows) {
                  allArrivalRows.push(...h.orderShuttleArrival.orderShuttlePlanRows);
                }
                if (h.orderShuttleDeparture?.orderShuttlePlanRows) {
                  allDepartureRows.push(...h.orderShuttleDeparture.orderShuttlePlanRows);
                }
              });
            }
          });

          // Deduplicate rows across crossovers
          const uniqueArrivalKeys = new Set<string>();
          allArrivalRows = allArrivalRows.filter(row => {
            const key = `${row.orderId}_${row.arrivalDepartureDate}`;
            if (uniqueArrivalKeys.has(key)) return false;
            uniqueArrivalKeys.add(key);
            return true;
          });

          const uniqueDepartureKeys = new Set<string>();
          allDepartureRows = allDepartureRows.filter(row => {
            const key = `${row.orderId}_${row.arrivalDepartureDate}`;
            if (uniqueDepartureKeys.has(key)) return false;
            uniqueDepartureKeys.add(key);
            return true;
          });

          // Re-bin all bookings day-by-day and hour-by-hour
          orderShuttlePlanReportVM.orderShuttlePlanDays.forEach(day => {
            const cleanHours: any[] = [];
            const baseDate = moment.tz(day.dayDate, "Europe/Stockholm").startOf('day');

            for (let h = 0; h < 24; h++) {
              const mFrom = baseDate.clone().hour(h).minute(0).second(0).millisecond(0);
              const mTo = baseDate.clone().hour(h).minute(59).second(59).millisecond(999);

              const arrivalsForHour = allArrivalRows.filter(row => {
                const d = moment(row.arrivalDepartureDate);
                return d.isSameOrAfter(mFrom) && d.isBefore(mTo);
              }).sort((a, b) => new Date(a.arrivalDepartureDate).getTime() - new Date(b.arrivalDepartureDate).getTime());

              const departuresForHour = allDepartureRows.filter(row => {
                const d = moment(row.arrivalDepartureDate);
                return d.isSameOrAfter(mFrom) && d.isBefore(mTo);
              }).sort((a, b) => new Date(a.arrivalDepartureDate).getTime() - new Date(b.arrivalDepartureDate).getTime());

              cleanHours.push({
                fromDate: mFrom.toDate(),
                toDate: mTo.toDate(),
                fromTimeStr: mFrom.format('HH:mm'),
                toTimeStr: mTo.format('HH:mm'),
                orderShuttleArrival: {
                  totalPassengers: arrivalsForHour.reduce((s, r) => s + (r.passengers || 0), 0),
                  totalBaggage: arrivalsForHour.reduce((s, r) => s + (r.baggage || 0), 0),
                  totalChildseats: arrivalsForHour.reduce((s, r) => s + (r.childseats || 0), 0),
                  totalCars: arrivalsForHour.reduce((s, r) => s + (r.cars || 0), 0),
                  totalOrders: new Set(arrivalsForHour.map(r => r.orderNr).filter(Boolean)).size,
                  totalFlights: new Set(arrivalsForHour.map(r => r.flightNr).filter(Boolean)).size,
                  orderShuttlePlanRows: arrivalsForHour
                },
                orderShuttleDeparture: {
                  totalPassengers: departuresForHour.reduce((s, r) => s + (r.passengers || 0), 0),
                  totalBaggage: departuresForHour.reduce((s, r) => s + (r.baggage || 0), 0),
                  totalChildseats: departuresForHour.reduce((s, r) => s + (r.childseats || 0), 0),
                  totalCars: departuresForHour.reduce((s, r) => s + (r.cars || 0), 0),
                  totalOrders: new Set(departuresForHour.map(r => r.orderNr).filter(Boolean)).size,
                  totalFlights: new Set(departuresForHour.map(r => r.flightNr).filter(Boolean)).size,
                  orderShuttlePlanRows: departuresForHour
                }
              });
            }

            day.orderShuttlePlanHours = cleanHours;
          });
        }
        this.orderShuttlePlanReportVM = orderShuttlePlanReportVM;
        this.isLoading = false;
      });
  }

  setDateFormats() {
    this.dateFrom = this.datepipe.transform(this.orderShuttlePlanningForm.controls['dateFrom'].value, 'yyyy-MM-dd');
    this.dateTo = this.datepipe.transform(this.orderShuttlePlanningForm.controls['dateTo'].value, 'yyyy-MM-dd');
  }

  planningDetails(orderShuttleArrivalDeparture: OrderShuttleArrivalDeparture): void {
    this.dialog.open(BackOffice24OrderShuttlePlanningDetailsComponent, {
      width: '920px',
      hasBackdrop: true,
      data: orderShuttleArrivalDeparture
    });
  }

  getTotalOrdersDep(planDay: OrderShuttlePlanDay) {
    let count = 0;
    for (let i = 0; i < planDay.orderShuttlePlanHours.length; i++) {
      count += planDay.orderShuttlePlanHours[i].orderShuttleDeparture.totalOrders;
    }
    return count;
  }

  getTotalPassengersDep(planDay: OrderShuttlePlanDay) {
    let count = 0;
    for (let i = 0; i < planDay.orderShuttlePlanHours.length; i++) {
      count += planDay.orderShuttlePlanHours[i].orderShuttleDeparture.totalPassengers;
    }
    return count;
  }

  getTotalBaggageDep(planDay: OrderShuttlePlanDay) {
    let count = 0;
    for (let i = 0; i < planDay.orderShuttlePlanHours.length; i++) {
      count += planDay.orderShuttlePlanHours[i].orderShuttleDeparture.totalBaggage;
    }
    return count;
  }

  getTotalChildseatsDep(planDay: OrderShuttlePlanDay) {
    let count = 0;
    for (let i = 0; i < planDay.orderShuttlePlanHours.length; i++) {
      count += planDay.orderShuttlePlanHours[i].orderShuttleDeparture.totalChildseats;
    }
    return count;
  }

  getTotalCarsDep(planDay: OrderShuttlePlanDay) {
    let count = 0;
    for (let i = 0; i < planDay.orderShuttlePlanHours.length; i++) {
      count += planDay.orderShuttlePlanHours[i].orderShuttleDeparture.totalCars;
    }
    return count;
  }

  getTotalFlightsDep(planDay: OrderShuttlePlanDay) {
    let count = 0;
    for (let i = 0; i < planDay.orderShuttlePlanHours.length; i++) {
      count += planDay.orderShuttlePlanHours[i].orderShuttleDeparture.totalFlights;
    }
    return count;
  }

  getTotalOrdersArr(planDay: OrderShuttlePlanDay) {
    let count = 0;
    for (let i = 0; i < planDay.orderShuttlePlanHours.length; i++) {
      count += planDay.orderShuttlePlanHours[i].orderShuttleArrival.totalOrders;
    }
    return count;
  }

  getTotalPassengersArr(planDay: OrderShuttlePlanDay) {
    let count = 0;
    for (let i = 0; i < planDay.orderShuttlePlanHours.length; i++) {
      count += planDay.orderShuttlePlanHours[i].orderShuttleArrival.totalPassengers;
    }
    return count;
  }

  getTotalBaggageArr(planDay: OrderShuttlePlanDay) {
    let count = 0;
    for (let i = 0; i < planDay.orderShuttlePlanHours.length; i++) {
      count += planDay.orderShuttlePlanHours[i].orderShuttleArrival.totalBaggage;
    }
    return count;
  }

  getTotalChildseatsArr(planDay: OrderShuttlePlanDay) {
    let count = 0;
    for (let i = 0; i < planDay.orderShuttlePlanHours.length; i++) {
      count += planDay.orderShuttlePlanHours[i].orderShuttleArrival.totalChildseats;
    }
    return count;
  }

  getTotalCarsArr(planDay: OrderShuttlePlanDay) {
    let count = 0;
    for (let i = 0; i < planDay.orderShuttlePlanHours.length; i++) {
      count += planDay.orderShuttlePlanHours[i].orderShuttleArrival.totalCars;
    }
    return count;
  }

  getTotalFlightsArr(planDay: OrderShuttlePlanDay) {
    let count = 0;
    for (let i = 0; i < planDay.orderShuttlePlanHours.length; i++) {
      count += planDay.orderShuttlePlanHours[i].orderShuttleArrival.totalFlights;
    }
    return count;
  }

  getFilteredHours(hours: any[]) {
    return hours;
  }

  shouldShowArrivalRow(hour: any): boolean {
    if (this.vehicleFilter === 'all') return true;
    const arrHasWithCars = hour.orderShuttleArrival?.orderShuttlePlanRows?.some((row: any) => row.cars > 0);
    const arrHasWithoutCars = hour.orderShuttleArrival?.orderShuttlePlanRows?.some((row: any) => row.cars === 0);
    if (this.vehicleFilter === 'with') {
      return arrHasWithCars;
    } else if (this.vehicleFilter === 'without') {
      return arrHasWithoutCars;
    }
    return true;
  }

  shouldShowDepartureRow(hour: any): boolean {
    if (this.vehicleFilter === 'all') return true;
    const depHasWithCars = hour.orderShuttleDeparture?.orderShuttlePlanRows?.some((row: any) => row.cars > 0);
    const depHasWithoutCars = hour.orderShuttleDeparture?.orderShuttlePlanRows?.some((row: any) => row.cars === 0);
    if (this.vehicleFilter === 'with') {
      return depHasWithCars;
    } else if (this.vehicleFilter === 'without') {
      return depHasWithoutCars;
    }
    return true;
  }

  getTotalOrdersArrFiltered(hours: any[]) {
    let count = 0;
    for (let i = 0; i < hours.length; i++) {
      if (this.shouldShowArrivalRow(hours[i])) {
        count += hours[i].orderShuttleArrival.totalOrders;
      }
    }
    return count;
  }

  getTotalPassengersArrFiltered(hours: any[]) {
    let count = 0;
    for (let i = 0; i < hours.length; i++) {
      if (this.shouldShowArrivalRow(hours[i])) {
        count += hours[i].orderShuttleArrival.totalPassengers;
      }
    }
    return count;
  }

  getTotalBaggageArrFiltered(hours: any[]) {
    let count = 0;
    for (let i = 0; i < hours.length; i++) {
      if (this.shouldShowArrivalRow(hours[i])) {
        count += hours[i].orderShuttleArrival.totalBaggage;
      }
    }
    return count;
  }

  getTotalChildseatsArrFiltered(hours: any[]) {
    let count = 0;
    for (let i = 0; i < hours.length; i++) {
      if (this.shouldShowArrivalRow(hours[i])) {
        count += hours[i].orderShuttleArrival.totalChildseats;
      }
    }
    return count;
  }

  getTotalCarsArrFiltered(hours: any[]) {
    let count = 0;
    for (let i = 0; i < hours.length; i++) {
      if (this.shouldShowArrivalRow(hours[i])) {
        count += hours[i].orderShuttleArrival.totalCars;
      }
    }
    return count;
  }

  getTotalFlightsArrFiltered(hours: any[]) {
    let count = 0;
    for (let i = 0; i < hours.length; i++) {
      if (this.shouldShowArrivalRow(hours[i])) {
        count += hours[i].orderShuttleArrival.totalFlights;
      }
    }
    return count;
  }

  getTotalOrdersDepFiltered(hours: any[]) {
    let count = 0;
    for (let i = 0; i < hours.length; i++) {
      if (this.shouldShowDepartureRow(hours[i])) {
        count += hours[i].orderShuttleDeparture.totalOrders;
      }
    }
    return count;
  }

  getTotalPassengersDepFiltered(hours: any[]) {
    let count = 0;
    for (let i = 0; i < hours.length; i++) {
      if (this.shouldShowDepartureRow(hours[i])) {
        count += hours[i].orderShuttleDeparture.totalPassengers;
      }
    }
    return count;
  }

  getTotalBaggageDepFiltered(hours: any[]) {
    let count = 0;
    for (let i = 0; i < hours.length; i++) {
      if (this.shouldShowDepartureRow(hours[i])) {
        count += hours[i].orderShuttleDeparture.totalBaggage;
      }
    }
    return count;
  }

  getTotalChildseatsDepFiltered(hours: any[]) {
    let count = 0;
    for (let i = 0; i < hours.length; i++) {
      if (this.shouldShowDepartureRow(hours[i])) {
        count += hours[i].orderShuttleDeparture.totalChildseats;
      }
    }
    return count;
  }

  getTotalCarsDepFiltered(hours: any[]) {
    let count = 0;
    for (let i = 0; i < hours.length; i++) {
      if (this.shouldShowDepartureRow(hours[i])) {
        count += hours[i].orderShuttleDeparture.totalCars;
      }
    }
    return count;
  }

  getTotalFlightsDepFiltered(hours: any[]) {
    let count = 0;
    for (let i = 0; i < hours.length; i++) {
      if (this.shouldShowDepartureRow(hours[i])) {
        count += hours[i].orderShuttleDeparture.totalFlights;
      }
    }
    return count;
  }

  getFilteredArrivalValues(hour: any) {
    const rows = hour.orderShuttleArrival?.orderShuttlePlanRows || [];
    const filtered = this.vehicleFilter === 'all' ? rows : 
      rows.filter((r: any) => this.vehicleFilter === 'with' ? r.cars > 0 : r.cars === 0);
    return {
      totalOrders: new Set(filtered.map((r: any) => r.orderNr).filter(Boolean)).size,
      totalPassengers: filtered.reduce((s: number, r: any) => s + (r.passengers || 0), 0),
      totalBaggage: filtered.reduce((s: number, r: any) => s + (r.baggage || 0), 0),
      totalChildseats: filtered.reduce((s: number, r: any) => s + (r.childseats || 0), 0),
      totalCars: filtered.reduce((s: number, r: any) => s + (r.cars || 0), 0),
      totalFlights: new Set(filtered.map((r: any) => r.flightNr).filter(Boolean)).size
    };
  }

  getFilteredDepartureValues(hour: any) {
    const rows = hour.orderShuttleDeparture?.orderShuttlePlanRows || [];
    const filtered = this.vehicleFilter === 'all' ? rows : 
      rows.filter((r: any) => this.vehicleFilter === 'with' ? r.cars > 0 : r.cars === 0);
    return {
      totalOrders: new Set(filtered.map((r: any) => r.orderNr).filter(Boolean)).size,
      totalPassengers: filtered.reduce((s: number, r: any) => s + (r.passengers || 0), 0),
      totalBaggage: filtered.reduce((s: number, r: any) => s + (r.baggage || 0), 0),
      totalChildseats: filtered.reduce((s: number, r: any) => s + (r.childseats || 0), 0),
      totalCars: filtered.reduce((s: number, r: any) => s + (r.cars || 0), 0),
      totalFlights: new Set(filtered.map((r: any) => r.flightNr).filter(Boolean)).size
    };
  }

  ngOnDestroy() {
    this.orgSubscription.unsubscribe();
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}